import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  ImageList,
  ImageListItem,
  Fab,
  Alert,
  Snackbar,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CameraAlt as CameraIcon,
  CloudUpload as UploadIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { 
  getPatientMoleMappings,
  getMacroImages,
  updateMoleMapping,
  getMappingAnnotations,
  uploadMicroImage,
  linkMicroToAnnotation,
  addAnnotation,
  AnnotationPayload,
  deleteMicroImage
} from '../../services/moleMappingApi';
import { MoleMappingData, MacroImage, Annotation, BaselineAnnotationContext } from './types';
import CameraDialog, { CameraDialogRef } from './components/CameraDialog';
import TourGuide from '../../../features/components/TourGuide';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useSSE } from './hooks/useSSE';
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

/**
 * Mobile-optimized version of MoleMappingSpace
 * Simplified UI focused on image capture
 */
const MoleMappingSpaceMobile: React.FC = () => {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();
  
  // Core states
  const [patientName, setPatientName] = useState<string>('');
  const [mappings, setMappings] = useState<MoleMappingData[]>([]);
  const [selectedMapping, setSelectedMapping] = useState<MoleMappingData | null>(null);
  const [macroImages, setMacroImages] = useState<MacroImage[]>([]);
  const [selectedMacroImage, setSelectedMacroImage] = useState<MacroImage | null>(null);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [selectedAnnotation, setSelectedAnnotation] = useState<Annotation | null>(null);
  const [baselineContext, setBaselineContext] = useState<BaselineAnnotationContext | null>(null);
  const [showBaselineMode, setShowBaselineMode] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // Camera states
  const [cameraOpen, setCameraOpen] = useState<boolean>(false);
  const [cameraMode, setCameraMode] = useState<'macro' | 'micro'>('micro'); // Track what we're capturing
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [cameraLoading, setCameraLoading] = useState<boolean>(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [availableCameras, setAvailableCameras] = useState<MediaDeviceInfo[]>([]);
  const [currentCameraIndex, setCurrentCameraIndex] = useState<number>(0);
  const [cameraInitAttempts] = useState<number>(0);
  
  // UI states
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [uploadedCount, setUploadedCount] = useState<number>(0);
  const [runTour, setRunTour] = useState<boolean>(false);
  
  // Replacement confirmation states
  const [showReplaceConfirmDialog, setShowReplaceConfirmDialog] = useState<boolean>(false);
  const [replacementFile, setReplacementFile] = useState<File | null>(null);
  
  // SSE states
  const [sseConnected, setSseConnected] = useState<boolean>(false);
  
  // Refs
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const macroFileInputRef = useRef<HTMLInputElement>(null);
  const microFileInputRef = useRef<HTMLInputElement>(null);
  const cameraDialogRef = useRef<CameraDialogRef>(null);
  
  // Load patient data
  useEffect(() => {
    console.log('MoleMappingSpaceMobile mounted with patientId:', patientId);
    console.log('API_BASE_URL:', API_BASE_URL);
    if (patientId) {
      setPatientName(`Patient ${patientId.substring(0, 8)}`);
      fetchMoleMappings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);
  
  // Debug FAB visibility
  useEffect(() => {
    console.log('FAB visibility check - selectedMapping:', selectedMapping?.id, 'loading:', loading);
  }, [selectedMapping, loading]);
  
  // Lock orientation to portrait on mobile
  useEffect(() => {
    const lockOrientation = async () => {
      try {
        // Check if Screen Orientation API is available
        const screenOrientation = window.screen.orientation as any;
        if (screenOrientation && screenOrientation.lock) {
          await screenOrientation.lock('portrait');
          console.log('Screen orientation locked to portrait');
        }
      } catch (error) {
        // Orientation lock might fail if not in fullscreen or unsupported
        console.log('Orientation lock not available or failed:', error);
      }
    };

    lockOrientation();

    // Cleanup: unlock orientation when component unmounts
    return () => {
      try {
        const screenOrientation = window.screen.orientation as any;
        if (screenOrientation && screenOrientation.unlock) {
          screenOrientation.unlock();
          console.log('Screen orientation unlocked');
        }
      } catch (error) {
        console.log('Orientation unlock failed:', error);
      }
    };
  }, []);
  
  // Fetch mole mapping sessions
  const fetchMoleMappings = useCallback(async () => {
    if (!patientId) return;
    
    console.log('Fetching mappings for patient:', patientId);
    setLoading(true);
    setError(null);
    
    try {
      const data = await getPatientMoleMappings(patientId);
      console.log('Received mappings:', data);
      setMappings(Array.isArray(data) ? data : []); // Ensure we always have an array
      
      // Auto-select most recent session
      if (data && data.length > 0) {
        const mostRecent = data.reduce((prev: any, current: any) => {
          return new Date(prev.createdAt) > new Date(current.createdAt) ? prev : current;
        });
        console.log('Auto-selecting most recent mapping:', mostRecent);
        setSelectedMapping(mostRecent);
        await loadMappingData(mostRecent.id);
      } else {
        console.log('No mappings found for patient');
        setSelectedMapping(null);
        setMacroImages([]);
      }
    } catch (error: any) {
      console.error('Error fetching mappings:', error);
      
      // More specific error handling
      if (error.response?.status === 404 || error.response?.status === 204) {
        // No mappings found for this patient - this is not an error
        setMappings([]);
        setSelectedMapping(null);
      } else if (error.response?.status === 401 || error.response?.status === 403) {
        setError('Authentication failed. Please sign in again.');
      } else if (error.code === 'NETWORK_ERROR' || error.message?.includes('Network Error')) {
        setError('Network connection error. Please check your internet connection.');
      } else {
        setError('Failed to load sessions. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [patientId]);
  
  // Get annotations with dermatoscopic images from current session
  const getAnnotationsWithImages = (): Annotation[] => {
    return annotations.filter(a => a.linkedMicroId);
  };
  
  // Load macro images for selected mapping
  const loadMappingData = async (mappingId: string) => {
    console.log('Loading mapping data for:', mappingId);
    setLoading(true);
    setError(null);
    
    try {
      const images = await getMacroImages(mappingId);
      console.log('Received images:', images);
      setMacroImages(Array.isArray(images) ? images : []);
    } catch (error: any) {
      console.error('Error loading mapping data:', error);
      
      if (error.response?.status === 404) {
        setError('Mapping session not found.');
      } else if (error.response?.status === 401) {
        setError('Authentication failed. Please sign in again.');
      } else {
        setError('Failed to load images. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Toggle follow-up mode
  const handleToggleBaselineMode = () => {
    setShowBaselineMode(!showBaselineMode);
    setBaselineContext(null);
  };
  
  // Handle annotation selection for follow-up
  const handleSelectBaselineAnnotation = (annotation: Annotation) => {
    if (!selectedMapping || !selectedMacroImage) return;
    
    console.log('Selected annotation for follow-up:', annotation.label);
    
    const context: BaselineAnnotationContext = {
      baselineAnnotation: annotation,
      baselineImage: selectedMacroImage,
      baselineSessionId: selectedMapping.id,
      followupSessionId: selectedMapping.id // Same session
    };
    
    setBaselineContext(context);
    setShowBaselineMode(false); // Hide list, show camera/upload
    
    setSnackbarMessage(`Ready to add follow-up image for "${annotation.label}"`);
    setSnackbarOpen(true);
  };
  
  // Handle session selection
  const handleSessionChange = async (event: any) => {
    const mappingId = event.target.value;
    const mapping = mappings.find(m => m.id === mappingId);
    
    if (mapping) {
      setSelectedMapping(mapping);
      setSelectedMacroImage(null);
      setAnnotations([]);
      setSelectedAnnotation(null);
      await loadMappingData(mapping.id);
    }
  };
  
  // Handle macro image selection
  const handleSelectMacroImage = async (image: MacroImage) => {
    console.log('Selecting macro image:', image.id);
    setSelectedMacroImage(image);
    setSelectedAnnotation(null);
    
    // Load annotations for this image
    try {
      const annotationsData = await getMappingAnnotations(image.id);
      console.log('Loaded annotations:', annotationsData);
      setAnnotations(Array.isArray(annotationsData) ? annotationsData : []);
    } catch (error) {
      console.error('Failed to load annotations:', error);
      setAnnotations([]);
    }
  };
  
  // Handle annotation selection
  const handleSelectAnnotation = (annotation: Annotation) => {
    console.log('Selected annotation:', annotation.label);
    setSelectedAnnotation(annotation);
  };
  
  // Open camera for macro or micro
  const openCamera = async (mode: 'macro' | 'micro' = 'micro') => {
    if (!selectedMapping) {
      setSnackbarMessage('Please select a session first');
      setSnackbarOpen(true);
      return;
    }
    
    setCameraMode(mode);
    
    try {
      setCameraOpen(true);
      setCameraLoading(true);
      setCameraError(null);
      
      // Enumerate cameras
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter(device => device.kind === 'videoinput');
      setAvailableCameras(cameras);
      
      // Request camera with back camera preference for mobile
      const constraints = {
        video: {
          facingMode: { ideal: 'environment' }, // Prefer back camera
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      setCameraStream(stream);
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      
      setCameraLoading(false);
    } catch (err: any) {
      console.error('Camera error:', err);
      setCameraError(err.message || 'Failed to access camera');
      setCameraLoading(false);
    }
  };
  
  // Switch camera
  const switchCamera = async () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
    }
    
    const nextIndex = (currentCameraIndex + 1) % availableCameras.length;
    setCurrentCameraIndex(nextIndex);
    
    try {
      const constraints = {
        video: {
          deviceId: availableCameras[nextIndex].deviceId,
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      setCameraStream(stream);
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (err: any) {
      setCameraError('Failed to switch camera');
    }
  };
  
  // Close camera
  const closeCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    
    setCameraOpen(false);
    setCameraLoading(false);
    setCameraError(null);
  };
  
  // Capture image from camera
  const captureImage = async () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(video, 0, 0);
      
      canvas.toBlob(async (blob) => {
        if (blob) {
          if (cameraMode === 'macro') {
            await uploadMacroImageFromBlob(blob);
          } else {
            // For micro images
            await uploadMicroImageFromBlob(blob);
          }
        }
      }, 'image/jpeg', 0.95);
    }
  };
  
  // Upload micro image from blob (captured from camera)
  const uploadMicroImageFromBlob = async (blob: Blob) => {
    if (!selectedAnnotation) return;
    
    const file = new File([blob], `micro_${Date.now()}.jpg`, { type: 'image/jpeg' });
    
    // Close camera first
    closeCamera();
    
    // Check if annotation already has an image and we're not in baseline context
    if (selectedAnnotation.linkedMicroId && !baselineContext) {
      setReplacementFile(file);
      setShowReplaceConfirmDialog(true);
      return;
    }
    
    setLoading(true);
    
    try {
      console.log('Uploading micro image for annotation:', selectedAnnotation.label);
      const response = await uploadMicroImage(file);
      console.log('Micro image upload response:', response);
      
      if (response && response.microImageId) {
        await linkMicroToAnnotation(selectedAnnotation.id, response.microImageId);
        
        const updatedAnnotation = {
          ...selectedAnnotation,
          linkedMicroId: response.microImageId,
          linkedMicroUrl: response.imageUrl
        };
        
        setAnnotations(prev => 
          prev.map(ann => 
            ann.id === selectedAnnotation.id ? updatedAnnotation : ann
          )
        );
        setSelectedAnnotation(updatedAnnotation);
        
        setSnackbarMessage('Microscopic image captured successfully!');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Failed to upload micro image:', error);
      setSnackbarMessage('Failed to upload microscopic image');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };
  
  // Upload macro image from blob (captured from camera)
  const uploadMacroImageFromBlob = async (blob: Blob) => {
    if (!selectedMapping) return;
    
    setLoading(true);
    closeCamera();
    
    try {
      const file = new File([blob], `macro_${Date.now()}.jpg`, { type: 'image/jpeg' });
      
      console.log('Uploading macro image for session:', selectedMapping.id);
      const notes = selectedMapping.notes || '';
      await updateMoleMapping(selectedMapping.id, notes, [file]);
      
      // Reload macro images to show the new one
      await loadMappingData(selectedMapping.id);
      setUploadedCount(prev => prev + 1);
      setSnackbarMessage('Macro image captured successfully!');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Failed to upload macro image:', error);
      setSnackbarMessage('Failed to upload macro image');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle macro image file upload
  const handleMacroImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0 || !selectedMapping) return;
    
    const file = event.target.files[0];
    setLoading(true);
    
    try {
      console.log('Uploading macro image for session:', selectedMapping.id);
      const notes = selectedMapping.notes || '';
      await updateMoleMapping(selectedMapping.id, notes, [file]);
      
      await loadMappingData(selectedMapping.id);
      setUploadedCount(prev => prev + 1);
      setSnackbarMessage('Macro image uploaded successfully!');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Failed to upload macro image:', error);
      setSnackbarMessage('Failed to upload macro image');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
      if (event.target) {
        event.target.value = '';
      }
    }
  };
  
  // Handle micro image file upload
  const handleMicroImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0 || !selectedAnnotation) return;
    
    const file = event.target.files[0];
    setReplacementFile(file);
    
    // Check if annotation already has an image and we're not in baseline context
    if (selectedAnnotation.linkedMicroId && !baselineContext) {
      setShowReplaceConfirmDialog(true);
    } else {
      // No existing image or baseline context - proceed with upload
      performMicroImageUpload(file);
    }
    
    // Reset file input
    if (microFileInputRef.current) {
      microFileInputRef.current.value = '';
    }
  };
  
  // Perform the actual micro image upload
  const performMicroImageUpload = async (file: File) => {
    if (!selectedAnnotation) return;
    
    // Only set loading if not already loading (called from replacement handler)
    const wasLoading = loading;
    if (!wasLoading) {
      setLoading(true);
    }
    
    try {
      console.log('Uploading micro image for annotation:', selectedAnnotation.label);
      const response = await uploadMicroImage(file);
      console.log('Micro image upload response:', response);
      
      if (response && response.microImageId) {
        // Check if this is a follow-up/baseline context
        if (baselineContext) {
          await handleBaselineFollowupUpload(response.microImageId, response.imageUrl);
        } else {
          // Regular annotation update - link the new image
          await linkMicroToAnnotation(selectedAnnotation.id, response.microImageId);
          
          const updatedAnnotation = {
            ...selectedAnnotation,
            linkedMicroId: response.microImageId,
            linkedMicroUrl: response.imageUrl
          };
          
          setAnnotations(prev => 
            prev.map(ann => 
              ann.id === selectedAnnotation.id ? updatedAnnotation : ann
            )
          );
          setSelectedAnnotation(updatedAnnotation);
          
          setSnackbarMessage('Microscopic image uploaded successfully!');
        }
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Failed to upload micro image:', error);
      setSnackbarMessage('Failed to upload microscopic image');
      setSnackbarOpen(true);
      throw error; // Re-throw so handleConfirmReplacement knows it failed
    } finally {
      if (!wasLoading) {
        setLoading(false);
      }
      if (microFileInputRef.current) {
        microFileInputRef.current.value = '';
      }
    }
  };
  
  // Handle baseline follow-up image upload
  const handleBaselineFollowupUpload = async (microImageId: string, imageUrl: string) => {
    if (!baselineContext || !selectedMapping || !selectedMacroImage) {
      console.error('Missing baseline context for follow-up upload');
      return;
    }
    
    try {
      // Create a new annotation in the current session that mirrors the baseline
      const followupAnnotationPayload: AnnotationPayload = {
        label: `${baselineContext.baselineAnnotation.label} (Follow-up)`,
        x: baselineContext.baselineAnnotation.x,
        y: baselineContext.baselineAnnotation.y,
        width: baselineContext.baselineAnnotation.width,
        height: baselineContext.baselineAnnotation.height,
        confidence: 1.0,
        orderIndex: annotations.length,
        macroImageId: selectedMacroImage.id
      };
      
      console.log('Creating follow-up annotation:', followupAnnotationPayload);
      const annotationResponse = await addAnnotation(followupAnnotationPayload);
      
      if (annotationResponse?.annotationId) {
        // Link the micro image to the new follow-up annotation
        await linkMicroToAnnotation(annotationResponse.annotationId, microImageId);
        
        // Create the new annotation object
        const newFollowupAnnotation: Annotation = {
          id: annotationResponse.annotationId,
          macroImageId: selectedMacroImage.id,
          label: followupAnnotationPayload.label,
          x: followupAnnotationPayload.x,
          y: followupAnnotationPayload.y,
          width: followupAnnotationPayload.width,
          height: followupAnnotationPayload.height,
          confidence: 1.0,
          orderIndex: followupAnnotationPayload.orderIndex ?? 0,
          linkedMicroId: microImageId,
          linkedMicroUrl: imageUrl,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        
        // Add to annotations list
        setAnnotations(prev => [...prev, newFollowupAnnotation]);
        setSelectedAnnotation(newFollowupAnnotation);
        
        // Clear baseline context
        setBaselineContext(null);
        
        setSnackbarMessage('Follow-up image added successfully!');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Failed to create follow-up annotation:', error);
      setSnackbarMessage('Failed to add follow-up image');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
      if (microFileInputRef.current) {
        microFileInputRef.current.value = '';
      }
    }
  };
  
  // Handle confirmation to replace existing micro image
  const handleConfirmReplacement = async () => {
    if (!replacementFile || !selectedAnnotation) return;
    
    setLoading(true);
    setShowReplaceConfirmDialog(false);
    
    try {
      // Delete the existing micro image if it exists
      if (selectedAnnotation.linkedMicroId) {
        console.log(`Deleting existing micro image: ${selectedAnnotation.linkedMicroId}`);
        await deleteMicroImage(selectedAnnotation.linkedMicroId);
        console.log('Successfully deleted existing micro image');
        
        // IMPORTANT: Update local state to remove the linked image
        // This ensures the annotation state is clean before uploading new image
        const cleanedAnnotation = {
          ...selectedAnnotation,
          linkedMicroId: null,
          linkedMicroUrl: null
        };
        setSelectedAnnotation(cleanedAnnotation);
        setAnnotations(prev => 
          prev.map(ann => 
            ann.id === selectedAnnotation.id ? cleanedAnnotation : ann
          )
        );
      }
      
      // Small delay to ensure state update propagates
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Upload the new image
      await performMicroImageUpload(replacementFile);
      
    } catch (error) {
      console.error('Failed to replace micro image:', error);
      setSnackbarMessage('Failed to replace image. Please try again.');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
      setReplacementFile(null);
    }
  };
  
  // Handle cancellation of replacement
  const handleCancelReplacement = () => {
    setShowReplaceConfirmDialog(false);
    setReplacementFile(null);
  };
  
  // Clean up camera on unmount
  useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [cameraStream]);
  
  // SSE Event Handler - Handle real-time updates
  const handleSSEEvent = useCallback((event: any) => {
    console.log('=== SSE EVENT RECEIVED (Mobile) ===');
    console.log('Event type:', event.type);
    console.log('Event data:', event.data);
    console.log('===================================');
    
    switch (event.type) {
      case 'connected':
        console.log('SSE connected for patient:', patientId);
        setSseConnected(true);
        setSnackbarMessage('Live updates connected');
        setSnackbarOpen(true);
        break;
        
      case 'session_created':
        console.log('New session created, refreshing mappings');
        fetchMoleMappings();
        setSnackbarMessage('New session created');
        setSnackbarOpen(true);
        break;
        
      case 'session_updated':
        console.log('Session updated, refreshing data');
        if (selectedMapping && selectedMapping.id === event.data.mapping_id) {
          loadMappingData(selectedMapping.id);
        }
        fetchMoleMappings();
        setSnackbarMessage('Session updated');
        setSnackbarOpen(true);
        break;
        
      case 'annotation_created':
        console.log('New annotation created');
        if (selectedMacroImage && selectedMacroImage.id === event.data.macro_image_id) {
          getMappingAnnotations(selectedMacroImage.id).then(annotationsData => {
            console.log('Refreshed annotations after SSE:', annotationsData);
            setAnnotations(annotationsData);
          });
        }
        setSnackbarMessage(`New annotation: ${event.data.label}`);
        setSnackbarOpen(true);
        break;
        
      case 'annotation_updated':
        console.log('Annotation updated');
        if (selectedMacroImage && selectedMacroImage.id === event.data.macro_image_id) {
          getMappingAnnotations(selectedMacroImage.id).then(annotationsData => {
            setAnnotations(annotationsData);
            
            // Update selected annotation if it was the one updated
            if (selectedAnnotation && selectedAnnotation.id === event.data.annotation_id) {
              const updatedAnnotation = annotationsData.find((a: Annotation) => a.id === event.data.annotation_id);
              if (updatedAnnotation) {
                setSelectedAnnotation(updatedAnnotation);
              }
            }
          });
        }
        setSnackbarMessage(`Annotation updated`);
        setSnackbarOpen(true);
        break;
        
      case 'annotation_deleted':
        console.log('Annotation deleted');
        if (selectedMacroImage && selectedMacroImage.id === event.data.macro_image_id) {
          getMappingAnnotations(selectedMacroImage.id).then(annotationsData => {
            setAnnotations(annotationsData);
            
            // If deleted annotation was selected, deselect it
            if (selectedAnnotation && selectedAnnotation.id === event.data.annotation_id) {
              setSelectedAnnotation(null);
            }
          });
        }
        setSnackbarMessage('Annotation deleted');
        setSnackbarOpen(true);
        break;
        
      case 'macro_image_uploaded':
        console.log('New macro image uploaded');
        if (selectedMapping && selectedMapping.id === event.data.mapping_id) {
          getMacroImages(selectedMapping.id).then(images => {
            setMacroImages(images);
          });
        }
        setSnackbarMessage('New macro image uploaded');
        setSnackbarOpen(true);
        break;
        
      case 'macro_image_deleted':
        console.log('Macro image deleted');
        if (selectedMapping) {
          getMacroImages(selectedMapping.id).then(images => {
            setMacroImages(images);
            
            // If deleted image was selected, clear selection
            if (selectedMacroImage && selectedMacroImage.id === event.data.macro_image_id) {
              if (images.length > 0) {
                handleSelectMacroImage(images[0]);
              } else {
                setSelectedMacroImage(null);
                setAnnotations([]);
              }
            }
          });
        }
        setSnackbarMessage('Macro image deleted');
        setSnackbarOpen(true);
        break;
        
      case 'micro_image_linked':
        console.log('Micro image linked to annotation');
        if (selectedMacroImage && selectedMacroImage.id === event.data.macro_image_id) {
          getMappingAnnotations(selectedMacroImage.id).then(annotationsData => {
            setAnnotations(annotationsData);
            
            // Update selected annotation if it was linked
            if (selectedAnnotation && selectedAnnotation.id === event.data.annotation_id) {
              const updatedAnnotation = annotationsData.find((a: Annotation) => a.id === event.data.annotation_id);
              if (updatedAnnotation) {
                setSelectedAnnotation(updatedAnnotation);
              }
            }
          });
        }
        setSnackbarMessage('Dermatoscopic image linked');
        setSnackbarOpen(true);
        break;
        
      case 'micro_image_deleted':
        console.log('Micro image deleted');
        if (selectedMacroImage && selectedMacroImage.id === event.data.macro_image_id) {
          getMappingAnnotations(selectedMacroImage.id).then(annotationsData => {
            setAnnotations(annotationsData);
            
            // Update selected annotation if affected
            if (selectedAnnotation && selectedAnnotation.id === event.data.annotation_id) {
              const updatedAnnotation = annotationsData.find((a: Annotation) => a.id === event.data.annotation_id);
              if (updatedAnnotation) {
                setSelectedAnnotation(updatedAnnotation);
              }
            }
          });
        }
        setSnackbarMessage('Dermatoscopic image deleted');
        setSnackbarOpen(true);
        break;
        
      default:
        console.log('Unhandled SSE event type:', event.type);
    }
  }, [patientId, selectedMapping, selectedMacroImage, selectedAnnotation, fetchMoleMappings]);
  
  const handleSSEError = useCallback((error: Event) => {
    console.error('SSE connection error:', error);
    setSseConnected(false);
  }, []);
  
  const handleSSEOpen = useCallback(() => {
    console.log('SSE connection opened (mobile)');
    setSseConnected(true);
  }, []);
  
  // Initialize SSE connection
  const sseEnabled = useMemo(() => !!patientId, [patientId]);
  
  const { disconnect: disconnectSSE } = useSSE({
    patientId: patientId || '',
    enabled: sseEnabled,
    onEvent: handleSSEEvent,
    onError: handleSSEError,
    onOpen: handleSSEOpen
  });
  
  // Clean up SSE on unmount
  useEffect(() => {
    return () => {
      console.log('Mobile component unmounting, disconnecting SSE');
      disconnectSSE();
    };
  }, [disconnectSSE]);
  
  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: 'background.default',
      pb: 10 // Space for floating buttons
    }}>
      {/* Header */}
      <Box sx={{ 
        position: 'sticky', 
        top: 0, 
        zIndex: 10, 
        bgcolor: 'background.paper',
        borderBottom: 1,
        borderColor: 'divider',
        p: 2
      }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <IconButton onClick={() => navigate('/patients')} edge="start">
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" noWrap>
              {patientName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Mole Mapping
              {sseConnected && (
                <Chip 
                  size="small" 
                  label="Live" 
                  color="success" 
                  sx={{ ml: 1, height: 16, fontSize: '0.65rem' }} 
                />
              )}
            </Typography>
          </Box>
          {uploadedCount > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CheckCircleIcon color="success" fontSize="small" />
              <Typography variant="body2" color="success.main">
                {uploadedCount}
              </Typography>
            </Box>
          )}
          <IconButton 
            onClick={() => setRunTour(true)} 
            size="small"
            data-tour="help-button"
            sx={{ color: 'primary.main' }}
          >
            <HelpOutlineIcon />
          </IconButton>
        </Stack>
      </Box>
      
      {/* Session Selector */}
      <Box sx={{ p: 2 }} data-tour="session-selector">
        {/* Debug Info - Remove in production */}
        <Alert severity="info" sx={{ mb: 2, fontSize: '0.75rem' }}>
          {/* <strong>Debug:</strong><br/> */}
          Mappings: {mappings.length}<br/>
          Selected: {selectedMapping ? 'Yes' : 'No'}<br/>
          Macro Images: {macroImages.length}<br/>
          {/* Loading: {loading ? 'Yes' : 'No'}<br/> */}
          {/* API URL: {API_BASE_URL || 'NOT SET'} */}
        </Alert>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {loading && mappings.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Loading sessions...
          </Alert>
        )}
        
        <FormControl fullWidth size="small">
          <InputLabel>Session</InputLabel>
          <Select
            value={selectedMapping?.id || ''}
            label="Session"
            onChange={handleSessionChange}
            disabled={loading || mappings.length === 0}
          >
            {mappings.map((mapping) => (
              <MenuItem key={mapping.id} value={mapping.id}>
                {dayjs(mapping.sessionDate).format('MMM D, YYYY')}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        {mappings.length === 0 && !loading && (
          <Alert severity="info" sx={{ mt: 2 }}>
            No sessions found. Please create one on desktop.
          </Alert>
        )}
        
        {selectedMapping && (
          <>
            <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
              Selected: {selectedMapping.id.substring(0, 8)}
            </Typography>
            
            {/* Follow-up Mode Toggle */}
            <Button
              variant={showBaselineMode ? "contained" : "outlined"}
              size="small"
              fullWidth
              sx={{ mt: 2 }}
              onClick={handleToggleBaselineMode}
              disabled={getAnnotationsWithImages().length === 0}
              data-tour="followup-button"
            >
              {showBaselineMode ? '✓ Follow-up Mode Active' : '📊 Add Follow-up Images'}
            </Button>
            
            {getAnnotationsWithImages().length === 0 && (
              <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'text.secondary', textAlign: 'center' }}>
                No annotations with dermatoscopic images in this session
              </Typography>
            )}
          </>
        )}
      </Box>
      
      {/* Follow-up Selection Mode */}
      {showBaselineMode && getAnnotationsWithImages().length > 0 && (
        <Box sx={{ p: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Select an annotation to add a follow-up dermatoscopic image
          </Alert>
          
          <Typography variant="subtitle2" gutterBottom>
            Annotations with Images
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {getAnnotationsWithImages().map((annotation) => (
              <Card 
                key={annotation.id}
                variant="outlined"
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'action.hover'
                  }
                }}
                onClick={() => handleSelectBaselineAnnotation(annotation)}
              >
                <CardContent sx={{ py: 1, px: 2, '&:last-child': { pb: 1 } }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" fontWeight="bold">
                      {annotation.label}
                    </Typography>
                    <Chip 
                      label="Add Follow-up" 
                      size="small" 
                      color="primary" 
                      sx={{ fontSize: '0.7rem' }}
                    />
                  </Stack>
                  {annotation.linkedMicroUrl && (
                    <Box sx={{ mt: 1 }}>
                      <img
                        src={`${API_BASE_URL}${annotation.linkedMicroUrl}`}
                        alt="Current"
                        style={{
                          width: '100%',
                          maxHeight: 120,
                          objectFit: 'cover',
                          borderRadius: 4
                        }}
                      />
                    </Box>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>
      )}
      
      {/* Follow-up Mode Banner */}
      {baselineContext && (
        <Box sx={{ p: 2, bgcolor: 'primary.light' }}>
          <Alert severity="success" icon={false}>
            <Typography variant="body2" fontWeight="bold">
              📸 Follow-up Mode: {baselineContext.baselineAnnotation.label}
            </Typography>
            <Typography variant="caption">
              Capture or upload a follow-up dermatoscopic image
            </Typography>
          </Alert>
        </Box>
      )}
      
      {/* Image Gallery */}
      {selectedMapping && (
        <Box sx={{ p: 2 }} data-tour="macro-gallery">
          <Typography variant="subtitle2" gutterBottom>
            Captured Images ({macroImages.length})
          </Typography>
          
          {macroImages.length > 0 ? (
            <ImageList cols={2} gap={8}>
              {macroImages.map((image) => (
                <ImageListItem 
                  key={image.id}
                  sx={{
                    cursor: 'pointer',
                    border: selectedMacroImage?.id === image.id ? '3px solid' : '1px solid',
                    borderColor: selectedMacroImage?.id === image.id ? 'primary.main' : 'divider',
                    borderRadius: 2,
                    overflow: 'hidden',
                    '&:hover': {
                      borderColor: 'primary.main',
                      opacity: 0.9
                    }
                  }}
                  onClick={() => handleSelectMacroImage(image)}
                >
                  <img
                    src={`${API_BASE_URL}${image.imageUrl}`}
                    alt={image.imageName || `Macro ${image.id.substring(0, 8)}`}
                    loading="lazy"
                    style={{
                      aspectRatio: '1',
                      objectFit: 'cover',
                      width: '100%',
                      height: '100%'
                    }}
                  />
                </ImageListItem>
              ))}
            </ImageList>
          ) : (
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">
                  No images yet. Start capturing!
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>
      )}
      
      {/* Annotations Section */}
      {selectedMacroImage && (
        <Box sx={{ p: 2 }} data-tour="annotations-section">
          <Typography variant="subtitle2" gutterBottom>
            Annotations ({annotations.length})
          </Typography>
          
          {annotations.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {annotations.map((annotation) => (
                <Card 
                  key={annotation.id}
                  variant="outlined"
                  sx={{
                    cursor: 'pointer',
                    border: selectedAnnotation?.id === annotation.id ? '2px solid' : '1px solid',
                    borderColor: selectedAnnotation?.id === annotation.id ? 'primary.main' : 'divider',
                    bgcolor: selectedAnnotation?.id === annotation.id ? 'action.selected' : 'background.paper'
                  }}
                  onClick={() => handleSelectAnnotation(annotation)}
                >
                  <CardContent sx={{ py: 1, px: 2, '&:last-child': { pb: 1 } }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" fontWeight="bold">
                        {annotation.label}
                      </Typography>
                      {annotation.linkedMicroId ? (
                        <Chip 
                          label="Has Image" 
                          size="small" 
                          color="success" 
                          sx={{ fontSize: '0.7rem' }}
                        />
                      ) : (
                        <Chip 
                          label="Add Image" 
                          size="small" 
                          color="warning" 
                          sx={{ fontSize: '0.7rem' }}
                        />
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  No annotations on this image.
                  <br />
                  Use desktop to add annotations.
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>
      )}
      
      {/* Microscopic Image Upload Section */}
      {selectedAnnotation && !showBaselineMode && (
        <Box sx={{ p: 2 }} data-tour="micro-upload-section">
          <Card sx={{ bgcolor: baselineContext ? 'success.light' : 'primary.light', color: 'primary.contrastText' }}>
            <CardContent>
              {baselineContext && (
                <Alert severity="success" sx={{ mb: 2, bgcolor: 'white' }}>
                  <strong>Follow-up Mode:</strong> Adding new image for baseline "{baselineContext.baselineAnnotation.label}"
                </Alert>
              )}
              
              <Typography variant="subtitle2" gutterBottom>
                📸 {baselineContext ? 'Add Follow-up Image' : 'Add Microscopic Image'}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {baselineContext 
                  ? `Baseline: ${baselineContext.baselineAnnotation.label}`
                  : `Selected: ${selectedAnnotation.label}`
                }
              </Typography>
              
              <Stack direction="row" spacing={1}>
                {/* <Button
                  variant="contained"
                  startIcon={<CameraIcon />}
                  onClick={() => openCamera('micro')}
                  fullWidth
                  disabled={loading}
                >
                  Camera
                </Button> */}
                <Button
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  onClick={() => microFileInputRef.current?.click()}
                  fullWidth
                  disabled={loading}
                  sx={{ bgcolor: 'white' }}
                >
                  Upload
                </Button>
              </Stack>
              
              {!baselineContext && selectedAnnotation.linkedMicroId && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" display="block" gutterBottom>
                    Current microscopic image:
                  </Typography>
                  <img
                    src={`${API_BASE_URL}${selectedAnnotation.linkedMicroUrl}`}
                    alt="Microscopic"
                    style={{
                      width: '100%',
                      borderRadius: 8,
                      marginTop: 8
                    }}
                  />
                </Box>
              )}
              
              {baselineContext && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" display="block" gutterBottom>
                    Baseline image:
                  </Typography>
                  <img
                    src={`${API_BASE_URL}${baselineContext.baselineAnnotation.linkedMicroUrl}`}
                    alt="Baseline"
                    style={{
                      width: '100%',
                      borderRadius: 8,
                      marginTop: 8
                    }}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
          
          {/* Hidden micro image input */}
          <input
            ref={microFileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleMicroImageUpload}
            style={{ display: 'none' }}
          />
        </Box>
      )}
      
      {/* Floating Action Buttons */}
      {selectedMapping && !cameraOpen && !loading && (
        <>
          {/* Macro Image - Camera */}
          {/* <Fab
            color="info"
            size="medium"
            sx={{
              position: 'fixed',
              bottom: 24,
              left: 24,
              zIndex: 9999,
              boxShadow: 6
            }}
            onClick={() => openCamera('macro')}
            aria-label="Capture macro image"
            data-tour="macro-camera-fab"
          >
            <CameraIcon />
          </Fab> */}
          
          {/* Macro Image - Upload */}
          <Fab
            variant="extended"
            color="info"
            size="medium"
            sx={{
              position: 'fixed',
              bottom: 96,
              left: 24,
              zIndex: 9999,
              boxShadow: 6
            }}
            onClick={() => macroFileInputRef.current?.click()}
            aria-label="Upload macro image"
            data-tour="macro-upload-fab"
          >
            <UploadIcon sx={{ mr: 1 }} />
            Macro Image
          </Fab>
          
          {/* Micro Image - Camera */}
          {/* <Fab
            color="secondary"
            size="large"
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: 9999,
              boxShadow: 6
            }}
            onClick={() => openCamera('micro')}
            aria-label="Capture microscopic image"
            data-tour="micro-camera-fab"
          >
            <BiotechIcon fontSize="large" />
          </Fab> */}
          
          {/* Micro Image - Upload */}
          {/* <Fab
            color="secondary"
            size="large"
            sx={{
              position: 'fixed',
              bottom: 96,
              right: 24,
              zIndex: 9999,
              boxShadow: 6
            }}
            onClick={() => microFileInputRef.current?.click()}
            data-tour="micro-upload-fab"
            aria-label="Upload microscopic image"
          >
            <UploadIcon fontSize="large" />
          </Fab> */}
          
          {/* Hidden macro file input */}
          <input
            ref={macroFileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleMacroImageUpload}
            style={{ display: 'none' }}
          />
        </>
      )}
      
      {/* Camera Dialog */}
      <CameraDialog
        ref={cameraDialogRef}
        open={cameraOpen}
        loading={cameraLoading}
        error={cameraError}
        cameraInitAttempts={cameraInitAttempts}
        availableCameras={availableCameras}
        currentCameraIndex={currentCameraIndex}
        videoRef={videoRef}
        canvasRef={canvasRef}
        onClose={closeCamera}
        onSwitchCamera={switchCamera}
        onCaptureImage={() => captureImage()}
      />
      
      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
      
      {/* Replace Micro Image Confirmation Dialog */}
      <Dialog
        open={showReplaceConfirmDialog}
        onClose={handleCancelReplacement}
      >
        <DialogTitle>Replace Existing Image?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This annotation already has a dermatoscopic image. Do you want to replace it with the new image?
            The old image will be permanently deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelReplacement} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleConfirmReplacement} color="primary" variant="contained">
            Replace
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Tour Guide */}
      <TourGuide
        steps={[
          {
            target: '[data-tour="help-button"]',
            content: 'Click here anytime to restart this tour and learn how to use the mobile interface',
            disableBeacon: true,
          },
          {
            target: '[data-tour="session-selector"]',
            content: 'Select a mole mapping session. Sessions must be created on the desktop version first.',
            disableBeacon: true,
          },
          {
            target: '[data-tour="macro-upload-fab"]',
            content: '⬆️ Blue upload button: Upload macro images of the body area. On mobile, this opens your camera or gallery.',
            disableBeacon: true,
          },
          {
            target: '[data-tour="macro-gallery"]',
            content: 'Tap on a macro image to view it and see its annotations. Note: Annotations must be added on desktop.',
            disableBeacon: true,
          },
          {
            target: '[data-tour="annotations-section"]',
            content: 'View annotations (mole markers) created on desktop. Desktop is required to add/edit annotations with AI segmentation. Important: Apply above 1% manual zoom for segmentation to work properly.',
            disableBeacon: true,
          },
          {
            target: '[data-tour="micro-upload-section"]',
            content: 'After selecting an annotation, use the Camera or Upload buttons to add a dermatoscopic image for that specific mole',
            disableBeacon: true,
          },
          {
            target: '[data-tour="followup-button"]',
            content: '📊 Follow-up Images: When an annotation already has a dermatoscopic image, click this to add follow-up images for comparison over time',
            disableBeacon: true,
          },
        ]}
        tourId="molemapping-mobile-tour"
        run={runTour}
        continuous={true}
        showSkipButton={true}
        showProgress={true}
        onFinish={() => setRunTour(false)}
        onSkip={() => setRunTour(false)}
      />
    </Box>
  );
};

export default MoleMappingSpaceMobile;
